import { isEqual } from 'lodash';
import { useEffect, useState, useCallback, createContext, useContext } from 'react';
import { useBrowser } from '../../common/services/BrowserService';
import { BreakpointIdentifier, ScreenSize } from '../../common/styles/constants';
import { ChildrenOnly } from '../../common/types/ChildrenOnly';

export const BreakPointMap = {
  [BreakpointIdentifier.SM]: ScreenSize.SMALL,
  [BreakpointIdentifier.MD]: ScreenSize.MEDIUM,
  [BreakpointIdentifier.LG]: ScreenSize.LARGE,
  [BreakpointIdentifier.XL]: ScreenSize.EXTRA_LARGE,
  [BreakpointIdentifier.XXL]: ScreenSize.EXTRA_EXTRA_LARGE,
};

const initState = {
  [BreakpointIdentifier.SM]: false,
  [BreakpointIdentifier.MD]: false,
  [BreakpointIdentifier.LG]: false,
  [BreakpointIdentifier.XL]: false,
  [BreakpointIdentifier.XXL]: false,
};

const BreakpointContext = createContext(initState);

export const BreakpointProvider = ({ children }: ChildrenOnly) => {
  const browser = useBrowser();

  const [breakpoints, setBreakpoints] = useState(initState);

  const onResize = useCallback(
    (event: Event) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const windowWidth = (event?.target as Window).innerWidth;
      const currentMap = Object.assign({}, breakpoints);

      for (const key in BreakPointMap) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const breakpoint = key as BreakpointIdentifier;
        const size = BreakPointMap[breakpoint];

        currentMap[breakpoint] = windowWidth <= size;
      }

      if (!isEqual(currentMap, breakpoints)) {
        setBreakpoints(currentMap);
      }
    },
    [breakpoints],
  );

  useEffect(() => {
    browser.window?.addEventListener('resize', onResize, { passive: true });

    browser.window?.dispatchEvent(new Event('resize'));

    return () => {
      browser.window?.removeEventListener('resize', onResize);
    };
  }, [browser.window, onResize]);

  return <BreakpointContext.Provider value={breakpoints}>{children}</BreakpointContext.Provider>;
};

/**
 * use Breakpoint returns a map of screen sizes and whether or not they are active.
 * Screen sizes returned include -
 *
 * @return Small, Medium, Large, XLarge, XXLarge
 */
export const useBreakpoints = () => useContext(BreakpointContext);
